import Debug from 'debug';
import short from 'short-uuid'
import { injectable, inject } from 'inversify'
import { StateMachine } from '@/app/shared/state-machine'
import { QuizStoreModule, QuizStoreModuleSymbol } from '@/app/quiz/state2';
import { PracticeStateMachine, PracticeStateMachineData } from '@/app/practice-player/domain';
import { ExamStateMachine, ExamStateMachineData } from '@/app/exam-player/domain';
import { QuizSessionQuery, QuizSessionQuerySymbol } from '@/app/quiz-session/store';
import { QuizSessionService, QuizSessionServiceSymbol } from '@/app/quiz-session/service';
import { PracticeSessionState } from '@/app/practice-session/service';
import { ExamSessionState } from '@/app/exam-session/service';
import * as States from './states'
import * as Data from './data'
import { LoadingFoundSession } from './states/quiz-player-loading-state';

const debug = Debug('quiz-player-state-machine')

type State = States.QuizPlayerLoadingState |
      States.QuizPlayerIntroState |
      States.QuizPlayerContinueState |
      States.QuizPlayerExamState |
      States.QuizPlayerPracticeState |
      States.QuizPlayerCanceledState |
      States.QuizPlayerErrorState

@injectable()
export class QuizPlayerStateMachine {

  private stateMachine: StateMachine<State, Data.QuizPlayerStateMachinePhase>

  private data: Data.QuizPlayerStateMachineData

  public onStartPracticeRequested?: () => void
  public onExamTimeExpired?: () => void
  public onContinueError?: () => void

  constructor(
    @inject(QuizStoreModuleSymbol)
    private quizStore: QuizStoreModule,
    @inject(PracticeStateMachine)
    private practiceStateMachine: PracticeStateMachine,
    @inject(ExamStateMachine)
    private examStateMachine: ExamStateMachine,
    @inject(QuizSessionQuerySymbol)
    private quizSessionQuery: QuizSessionQuery,
    @inject(QuizSessionServiceSymbol)
    private quizSessionService: QuizSessionService) {

    this.stateMachine = new StateMachine<State, Data.QuizPlayerStateMachinePhase>(
      'quiz-player-state-machine')
    this.examStateMachine.onTimeExpired = () => {
      if (this.onExamTimeExpired) { this.onExamTimeExpired() }
    }
  }

  initialize(data: Data.QuizPlayerStateMachineData,
             practiceData: PracticeStateMachineData,
             examData: ExamStateMachineData) {
    debug('initialize')
    this.data = data
    this.practiceStateMachine.initialize(practiceData)
    this.examStateMachine.initialize(examData)
  }
  
  start(quizId: string, aspect: string) {
    debug('start', quizId)

    this.goToLoadingState(quizId, aspect)
  }

  private goToLoadingState(quizId: string, aspect: string) {
    const loadQuizJob = new States.LoadQuizJob(this.quizStore)
    const events = {
      ...this.commonEventHandlers,
      onLoaded: () => {
        this.goToIntroState()
      },
      onActiveSessionFound: (session: LoadingFoundSession) => {
        this.goToContinueState(session)
      } 
    }
    
    const loadingState = new States.QuizPlayerLoadingState(quizId, aspect, this.data, loadQuizJob,
                                                    this.quizSessionQuery)
    this.stateMachine.changeState(loadingState, events)
  }

  startPractice(questionCount: number) {
    debug(`startPractice in '${this.currentStateName}' state`, questionCount)
    this.stateMachine.executeAction('startQuiz', {
      mode: 'practice',
      questionCount
    })
  }

  startExam() {
    debug(`startExam in '${this.currentStateName}' state`)
    this.stateMachine.executeAction('startQuiz', {
      mode: 'exam',
    })
  }

  tryAgain(mode: States.QuizMode) {
    debug(`tryAgain in '${this.currentStateName}' state`, mode)
    this.stateMachine.executeAction('tryAgain', mode)
  }

  abortFoundSession() {
    debug(`abortFoundSession in '${this.currentStateName}' state`)
    this.stateMachine.executeAction('abortFoundSession')
  }

  continueFoundSession() {
    debug(`continueFoundSession in '${this.currentStateName}' state`)
    this.stateMachine.executeAction('continueFoundSession')
  }

  cancel() {
    debug(`cancel in '${this.currentStateName}' state`)
    this.stateMachine.executeAction('cancel')
  }

  executeChildAction(action: (stateMachine: States.ChildStateMachine) => void) {
    this.stateMachine.executeAction('executeChildAction', action)
  }

  executePracticeAction(action: (stateMachine: PracticeStateMachine) => void) {
    this.stateMachine.executeAction('executePracticeAction', action)
  }

  private goToContinueState(session: Data.FoundSession) {
    const events = {
      ...this.commonEventHandlers,
      onContinue: (session: Data.FoundSession) => {
        this.handleContinueQuiz(session)
      },
      onIntro: () => {
        this.goToIntroState()
      }
    }
    const continueState = new States.QuizPlayerContinueState(session, this.data, this.quizSessionQuery,
                                                      this.quizSessionService)
    this.stateMachine.changeState(continueState, events)
  }

  private goToIntroState() {
    const events = {
      ...this.commonEventHandlers,
      onStart: (options: States.QuizStartOptions) => this.handleStartQuiz(options)
    }
    const introState = new States.QuizPlayerIntroState(this.data)

    this.stateMachine.changeState(introState, events)
  }

  private handleContinueQuiz(session: Data.FoundSession) {
    switch (session.type) {
      case 'practice':
        this.gotoPracticeState({ type: 'continue', sessionId: session.id,
                                 state: session.state as PracticeSessionState })
        break
      case 'exam':
        this.gotoExamState({ type: 'continue', sessionId: session.id,
                             state: session.state as ExamSessionState })
    }
  }

  private handleStartQuiz(options: States.QuizStartOptions) {
    const sessionId = this.generateSessionId()
    
    switch (options.mode) {
      case 'practice':
        this.gotoPracticeState({ type: 'new', sessionId, questionCount: options.questionCount})
        break
      case 'exam':
        this.gotoExamState({ type: 'new', sessionId: sessionId })
        break
    }
  }

  private gotoExamState(startOptions: States.QuizExamStartOptions) {
    const events = {
      ...this.commonEventHandlers,
      onTryAgain: (mode: States.QuizMode) => this.handleTryAgain(mode),
      onContinueError: () => this.handleContinueError()
    }
    
    const examState = new States.QuizPlayerExamState(this.data,
                                              startOptions,
                                              this.examStateMachine)
    this.stateMachine.changeState(examState, events)
  }

  private gotoPracticeState(startOptions: States.QuizPracticeStartOptions) {
    const events = {
      ...this.commonEventHandlers,
      onTryAgain: (mode: States.QuizMode) => this.handleTryAgain(mode),
      onContinueError: () => this.handleContinueError()
    }
    const practiceState = new States.QuizPlayerPracticeState(startOptions,
                                                      this.data,
                                                      this.practiceStateMachine)
    this.stateMachine.changeState(practiceState, events)
  }

  private handleCanceled() {
    const canceledState = new States.QuizPlayerCanceledState(this.data)
    this.stateMachine.changeState(canceledState, this.commonEventHandlers)
  }

  private handleError(err: unknown) {
    const errorState = new States.QuizPlayerErrorState(this.data, err)
    this.stateMachine.changeState(errorState, this.commonEventHandlers)
  }

  private get commonEventHandlers(): States.QuizPlayerStateMachineCommonEvents {
    return {
      onCanceled: () => this.handleCanceled(),
      onError: (err) => this.handleError(err)
    }
  }

  private handleContinueError() {
    this.goToLoadingState(this.data.quizId, this.data.aspect)
    if (this.onContinueError) {
      this.onContinueError()
    }
  }

  private handleTryAgain(mode: States.QuizMode) {
    switch (mode) {
      case 'exam':
        this.gotoExamState({ type: 'new', sessionId: this.generateSessionId() })
        break
      case 'practice':
        this.goToIntroState()
        if (this.onStartPracticeRequested) {
          this.onStartPracticeRequested()
        }
        break
    }
  }

  private get currentStateName() {
    return this.stateMachine.currentStateName
  }

  private generateSessionId(): string {
    return short.generate()
  }
}
